import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useGmtClock } from '../hooks/useGmtClock.js';
import { MEGA_NAV } from '../data/megaNav.js';
import { SECTION_BY_PAGE } from '../data/pagesContent.js';
import {
  getSiteLang,
  LANG_OPTIONS,
  langLabel,
  switchSiteLanguage,
} from '../utils/siteLang.js';
import { withBasePath } from '../utils/paths.js';

function Chevron() {
  return (
    <svg className="nav__mega-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(min-width: 1025px)').matches &&
  window.matchMedia('(hover: hover)').matches;

function MegaItem({ item, openId, setOpenId, onNavigate, currentSection }) {
  const megaId = `mega-${item.id}`;
  const isOpen = openId === item.id;
  const isCurrent = currentSection === item.section || (item.section === 'home' && currentSection === 'home');

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenId(isOpen ? null : item.id);
  };

  return (
    <li
      className={`nav__item nav__item--mega${isCurrent ? ' nav__item--current' : ''}${isOpen ? ' is-open' : ''}`}
      onMouseEnter={() => {
        if (canHover()) setOpenId(item.id);
      }}
      onMouseLeave={() => {
        if (canHover()) setOpenId((cur) => (cur === item.id ? null : cur));
      }}
    >
      <div className="nav__item-top">
        <NavLink
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) => `nav__link nav__link--parent${isActive ? ' is-active' : ''}`}
          onClick={onNavigate}
        >
          {item.label}
        </NavLink>
        <button
          type="button"
          className="nav__mega-toggle"
          aria-expanded={isOpen}
          aria-controls={megaId}
          aria-label={`${item.label} submenu`}
          onClick={toggle}
        >
          <Chevron />
        </button>
      </div>

      <div className="nav__mega" id={megaId} role="region" aria-label={`${item.label} submenu`}>
        <div className="nav__mega-inner container">
          <div className="nav__mega-layout">
            <div className="nav__mega-grid">
              {item.columns.map((col) => (
                <div key={col.heading} className="nav__mega-col">
                  <p className="nav__mega-heading">{col.heading}</p>
                  <ul className="nav__mega-list">
                    {col.links.map((link) => (
                      <li key={link.to + link.label}>
                        <Link to={link.to} onClick={onNavigate}>
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {item.promo && (
              <Link to={item.promo.to} className="nav__mega-promo" onClick={onNavigate}>
                <img
                  className="nav__mega-promo__img"
                  src={item.promo.img}
                  alt=""
                  width="640"
                  height="400"
                  loading="lazy"
                  decoding="async"
                />
                <span className="nav__mega-promo__overlay">
                  <span className="nav__mega-promo__label">{item.promo.label}</span>
                  <span className="nav__mega-promo__text">{item.promo.text}</span>
                  <span className="nav__mega-promo__cta">{item.promo.cta}</span>
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function currentNavSection(pathname) {
  if (pathname === '/' || pathname === '') return 'home';
  const slug = pathname.replace(/^\//, '').split(/[?#]/)[0];
  return SECTION_BY_PAGE[slug] || '';
}

export default function Navbar({ menuOpen, setMenuOpen }) {
  const { time, date } = useGmtClock();
  const { pathname } = useLocation();
  const isHome = pathname === '/' || pathname === '';
  const section = currentNavSection(pathname);
  const [openMega, setOpenMega] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState(() => getSiteLang());
  const [scrolled, setScrolled] = useState(false);
  const langRootRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('is-nav-open', menuOpen);
    return () => document.body.classList.remove('is-nav-open');
  }, [menuOpen]);

  // Close any open menus when the route changes. The panel is driven by state
  // (not CSS :hover), so it stays closed until the next hover/focus — the first
  // hover on the new page opens it reliably.
  useEffect(() => {
    setOpenMega(null);
    setMenuOpen(false);
    setLangOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [pathname]);

  useEffect(() => {
    if (!langOpen) return undefined;
    const onDocClick = (e) => {
      if (langRootRef.current && !langRootRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const onPickLang = (code) => {
    if (code === getSiteLang()) {
      setLangOpen(false);
      return;
    }
    setLang(code);
    setLangOpen(false);
    switchSiteLanguage(code);
  };

  const solid = !isHome || scrolled;
  const logoCompact = scrolled;

  const closeAll = () => {
    setMenuOpen(false);
    setOpenMega(null);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const goHomeTop = () => {
    closeAll();
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <header
      className={`site-header${solid ? ' is-scrolled' : ''}${logoCompact ? ' is-logo-compact' : ''}`}
      id="siteHeader"
      role="banner"
    >
      <div className="header__inner">
        <div className="header__logo">
          <Link to="/" className="logo" aria-label="Afroport Mauritanie SARL home" onClick={goHomeTop}>
            <img
              src={withBasePath('/assets/logo.png')}
              alt="Afroport Mauritanie SARL"
              className="logo__img"
              width="200"
              height="48"
            />
          </Link>
        </div>

        <nav className={`nav${menuOpen ? ' is-open' : ''}`} aria-label="Main navigation">
          <ul className="nav__list">
            <li className={`nav__item${section === 'home' ? ' nav__item--current' : ''}`}>
              <NavLink
                to="/"
                end
                className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
                onClick={goHomeTop}
              >
                Home
              </NavLink>
            </li>
            {MEGA_NAV.map((item) => (
              <MegaItem
                key={item.id}
                item={item}
                openId={openMega}
                setOpenId={setOpenMega}
                onNavigate={closeAll}
                currentSection={section}
              />
            ))}
          </ul>
        </nav>

        <div className="header__actions">
          <div className="header-meta notranslate" translate="no" aria-label="Nouakchott local time">
            <div className="header-meta__datetime">
              <time className="header-meta__date">{date}</time>
              <span className="header-meta__clock-row">
                <span className="header-meta__time">{time}</span>
                <span className="header-meta__tz">GMT</span>
              </span>
            </div>
          </div>

          <div className="lang-dropdown notranslate" translate="no" ref={langRootRef}>
            <button
              type="button"
              className="lang-dropdown__btn"
              id="langDropdownBtn"
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-controls="langDropdownList"
              onClick={(e) => {
                e.stopPropagation();
                setLangOpen((v) => !v);
              }}
            >
              <span className="lang-dropdown__current" id="langDropdownCurrent">
                {langLabel(lang)}
              </span>
              <svg className="lang-dropdown__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <ul
              className="lang-dropdown__menu"
              id="langDropdownList"
              role="listbox"
              aria-labelledby="langDropdownBtn"
              hidden={!langOpen}
            >
              {LANG_OPTIONS.map((opt) => (
                <li key={opt.code} role="none">
                  <button
                    type="button"
                    className={`lang-dropdown__option${lang === opt.code ? ' lang-dropdown__option--active' : ''}`}
                    role="option"
                    data-lang={opt.code}
                    data-label={opt.label}
                    lang={opt.code}
                    aria-selected={lang === opt.code}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPickLang(opt.code);
                    }}
                  >
                    {opt.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            className="nav-toggle"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((v) => !v);
              setOpenMega(null);
            }}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
  );
}
